import * as React from 'react'
import { useQuery } from 'react-query'
import { PullResponse } from './types'
import { GithubClient } from 'api/clients/GithubClient'
import { GithubCard } from './GithubCard'
import { GithubFrame } from './GithubFrame'
import { GithubInline } from './GithubInline'

import { EmbedError } from '../EmbedCommon/EmbedError'
import { FetchError } from './common'
import { EmbedPrivate } from '../EmbedCommon/EmbedPrivate'
import { StrongNode, nodes } from 'prose-core'
import store from 'redux/store'
import { useInterval } from 'beautiful-react-hooks'
import { ReactComponent as GithubIcon } from 'assets/icons/logos/github24.svg'

export type GithubIssueProps = {
  node: StrongNode<typeof nodes.github_embed>
  preview?: true
}

export const GithubIssue: React.FC<React.PropsWithChildren<GithubIssueProps>> = (props) => {
  const { node, preview } = props
  const { repo, id } = /(?<repo>[^/]+?\/[^/]+?)\/[^/]+?\/(?<id>[^/]+?)$/.exec(node.attrs.link)?.groups ?? {}
  const [shouldRetry, setShouldRetry] = React.useState(false)
  const maybeArticle = store.getState().currentArticleId
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      store.getState().articleMeta[store.getState().currentArticleId!]
    : null

  const { isLoading, data, isError, refetch, error } = useQuery(
    [maybeArticle?.company_id, 'issue', repo, id],
    async () => {
      if (!maybeArticle?.company_id) {
        throw new Error('missing company_id (are you inside a workspace?)')
      }
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const githubClientResponse: any = await GithubClient.getResource(maybeArticle!.company_id!, 'issue', repo, id)
      if (githubClientResponse.status >= 400) {
        throw new FetchError(githubClientResponse, 'Error')
      }
      return githubClientResponse.data as PullResponse
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: () => {
        if (isError) {
          // if the request is not successful, refetchOnWindowFocus only if the timeout has elapsed
          if (shouldRetry) {
            setShouldRetry(false)
            return true
          } else {
            return false
          }
        }
        // if the request is successful, respect staleTime
        return true
      },
    },
  )

  useInterval(() => {
    if (isError) {
      setShouldRetry(true)
    }
  }, 10 * 60 * 1000)

  if (isError && [401, 404].includes((error as FetchError)?.res?.status)) {
    return <EmbedPrivate mode={node.attrs.mode} link={node.attrs.link} preview={preview} icon={GithubIcon} />
  } else if (isError || (!isLoading && !data)) {
    return <EmbedError refetch={refetch} mode={node.attrs.mode} link={node.attrs.link} preview={preview} />
  }

  const status = (() => {
    switch (data?.state) {
      default:
      case 'open':
        return 'open-issue'
      case 'closed':
        return 'closed-issue'
    }
  })()

  switch (node?.attrs.mode) {
    default:
    case 'frame':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubFrame node={node} data={data!} isLoading={isLoading} status={status} />
    case 'card':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubCard node={node} data={data!} isLoading={isLoading} status={status} />
    case 'inline':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubInline node={node} data={data!} isLoading={isLoading} status={status} />
  }
}
